import { closePanel } from "@/layouts/layouts";
import { computed, ref, Ref, nextTick } from "vue";
import Bus from "@/core/bus";
import { toastError } from "@/helpers/toastify";

const PROPS_ERROR = "Не заполнены необходимые поля";

export interface ControllerProps {
  store: Record<string, any>;
  config: Record<string, any>;
  data: Record<string, any> | Ref<Record<string, any>>;
  params: Record<string, any>;
  panel?: Record<string, any>;
}

// Класс-контроллер для управления формой редактирования (кнопки, сохранение, валидация, доступы)
export default class EditController {
	public readonly readonly: Ref<boolean>;
	protected _store?: Ref<any>;
	protected _data?: Ref<any>;
	// Флаг для предотвращения повторных вызовов save
	protected _isSaving: boolean = false;

	public readonly panelFun = ref < any[] > ([]);
	public readonly standardCommands: Record<string, any>;

  constructor(protected form: Ref<any>, protected props: ControllerProps) {
    this.readonly = computed(() => {
      // Проверяем props.store, так как он может быть undefined при инициализации
      if (props.store && typeof props.store === 'object' && 'state' in props.store) {
        return props.store.state?.readonly ?? false;
      }
      // Если store еще не инициализирован, возвращаем false
      return false;
    });

    this.standardCommands = {
      saveClose: {
        caption: "Сохранить и закрыть",
        title: "Сохранить и закрыть",
        icon: <i class="icon icon-check-circle"></i>,
        class: "btn btn-save",
        onClick: async () => this.props.panel?.close?.(await this.save()),
      },
      save: {
        title: "Сохранить",
        icon: <i class="icon icon-save"></i>,
        class: "btn btn-save",
        onClick: async () => await this.save(),
      },
      close: {
        caption: "Закрыть",
        title: "Закрыть",
        class: "btn btn-close",
        onClick: () => closePanel(this.props.panel),
      },
    };

    // Для модальных окон кнопки устанавливаются через setButtons() в modal-footer
    // Для немодальных окон используем panelFun
    if (!props.panel?.modal) {
      // Инициализируем panelFun с кнопкой "Закрыть" сразу для немодальных окон
      this.createPanelFun([{ close: {} }]);

      // Обновляем panelFun после инициализации store с проверкой прав
      nextTick(() => {
        const newPanelFun: any[] = [];

        // Проверяем права через методы store
        const store = this.store?.value || props.store;
        const canUpdate = store?.accessUpdate?.() || false;
        const canCreate = store?.accessCreate?.() || false;

        if (!this.readonly.value && (canUpdate || canCreate)) {
          newPanelFun.push({ saveClose: {}, save: {} });
        }

        newPanelFun.push({ close: {} });

        this.createPanelFun(newPanelFun);
      });
    }

    // Защита метода save от переопределения
    Object.defineProperty(this, "save", {
      value: this.save.bind(this),
      writable: false,
      configurable: false,
    });

    // Устанавливаем onBeforeSave в модель после инициализации
    // Используем nextTick, чтобы убедиться, что модель уже инициализирована
    nextTick(() => {
      if (this.store?.value?.model) {
        this.store.value.model.onBeforeSave = async (data: any, isNew: boolean) => {
          try {
            // Если есть метод customSave, вызываем его, иначе вызываем beforeSave
            if (typeof (this as any).customSave === 'function') {
              return await (this as any).customSave();
            } else {
              return await this.beforeSave();
          }
          } catch (error) {
            throw error;
          }
        };
      }
    });
  }

	get store(): Ref<any> {
		if (!this._store) {
			this._store = computed(() => {
				// Проверяем, что form инициализирован и имеет store
				if (this.form?.value?.store) {
					return this.form.value.store;
				}
				// Если store еще не инициализирован, возвращаем null
				return null;
			});
		}
		return this._store;
	}

	get data(): Ref<any> {
		if (!this._data) {
			this._data = computed(() => this.form?.value?.data ?? {});
		}
		return this._data;
	}

  private getResolvedStore(): any {
    const storeFromForm = this.form?.value?.store;
    if (storeFromForm) return storeFromForm;

    const computedStore = this.store?.value;
    if (computedStore) return computedStore;

    // В ряде форм store приходит как ComputedRef в props
    const propsStoreValue = (this.props as any)?.store?.value;
    if (propsStoreValue) return propsStoreValue;

    return this.props?.store ?? null;
  }

  private async waitForInitialization(timeoutMs = 10000): Promise<boolean> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      const formInstance = this.form?.value;
      const resolvedStore = this.getResolvedStore();

      if (
        formInstance &&
        typeof formInstance.save === "function" &&
        resolvedStore
      ) {
        return true;
      }

      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return false;
  }

	public createPanelFun(items: any[]) {
		this.panelFun.value.splice(0, this.panelFun.value.length);

    for (const item of items) {
      const panel: any = {};

      for (const key in item) {
        panel[key] = {};

        if (key in this.standardCommands) {
          Object.assign(panel[key], this.standardCommands[key], item[key]);
        } else {
          Object.assign(panel[key], item[key]);
        }
      }

      this.panelFun?.value?.push(panel);
    }
  }

	public setButtons() {
		const buttons: any[] = [];

    // Проверяем права через методы store, которые учитывают роль пользователя
    const store = this.store?.value || this.props.store;
    const canUpdate = store?.accessUpdate?.() || false;
    const canCreate = store?.accessCreate?.() || false;

    if (!this.readonly.value && (canUpdate || canCreate)) {
      buttons.push({
        caption: "Сохранить и закрыть",
        class: "btn-action",
        onClick: async () => {
          await this.save();
          this.props.panel?.close?.();
        },
      });

      buttons.push({
        caption: "Сохранить",
        class: "btn-action",
        onClick: async () => await this.save(),
      });
    }

    // Для модальных окон кнопка "Закрыть" уже есть в Modals.vue (строка 26) если panel.closeable
    // Для немодальных окон кнопка "Закрыть" добавляется через panelFun
    // Поэтому здесь не добавляем кнопку "Закрыть" ни для модальных, ни для немодальных окон

    // Устанавливаем кнопки через panel.setButtons для модальных окон
    // Для немодальных окон кнопки управляются через panelFun
    if (this.props.panel?.modal) {
      this.props.panel?.setButtons?.(buttons);
    }
  }

	// Метод save больше нельзя переопределить
	protected async save(noValidation = false): Promise<boolean> {
    if (this._isSaving) {
      return false;
    }

    this._isSaving = true;

		try {
      const initialized = await this.waitForInitialization();
      if (!initialized) {
        console.error("Form/store initialization timeout", {
          form: this.form?.value,
          store: this.store,
          propsStore: this.props?.store,
        });
        toastError("Форма еще не инициализирована. Повторите сохранение.");
        return false;
      }

      // Сначала вызываем базовую валидацию
      if (!noValidation) {
        const isValid = await this.validationBase();
        if (!isValid) {
          return false;
        }
      }

      const resolvedStore = this.getResolvedStore();

      // Защита от зацикливания: временно отключаем onBeforeSave перед вызовом save
      // Это нужно, если customSave вызывает this.save(), который снова вызовет onBeforeSave
      const originalOnBeforeSave = resolvedStore?.model?.onBeforeSave;
      if (resolvedStore?.model && typeof (this as any).customSave === "function") {
        resolvedStore.model.onBeforeSave = async () => true;
      }

      try {
      const saveResult = await this.form.value?.save?.(noValidation);
        
        // Восстанавливаем оригинальный onBeforeSave
        if (resolvedStore?.model && originalOnBeforeSave) {
          resolvedStore.model.onBeforeSave = originalOnBeforeSave;
        }

      const storeValue = resolvedStore;
      if (storeValue?.owner && storeValue?.name) {
        Bus.$emit("save-data-child", {
          table: storeValue.name,
          newrecord: false,
        });
      }

      return saveResult;
      } catch (error) {
        // Восстанавливаем оригинальный onBeforeSave даже при ошибке
        if (resolvedStore?.model && originalOnBeforeSave) {
          resolvedStore.model.onBeforeSave = originalOnBeforeSave;
        }
        throw error;
      }
    } catch (error) {
      this.handleError(error);
      return false;
    } finally {
      this._isSaving = false;
    }
  }

	protected async beforeSave(): Promise<boolean> {
		return true;
	}

  // Публичный метод для вызова приватного save
  public async triggerSave(noValidation = false): Promise<boolean> {
    return this.save(noValidation);
  }

	// Базовый метод валидации для UI
	protected async validationBase(): Promise<boolean> {
		// Сначала вызываем кастомную валидацию
		const isCustomValid = await this.validate();
		if (isCustomValid === false) {
      return false;
    }
		// Потом вызываем валидацию формы для UI
		const isFormValid = await this.form.value?.validation?.();
    if (isFormValid === false) {
      return false;
    }

    return true;
	}

	// Кастомный метод валидации, который можно переопределять
	protected async validate(): Promise<boolean> {
		return true;
	}

	protected handleError(error: any): void {
		console.error("Ошибка при сохранении:", error);
		toastError("Ошибка при сохранении данных.");
	}

	public settings() {
		return Object.assign({}, this.props.config);
	}

	public setup() {
		return {
			readonly: this.readonly,
			controller: this,
			form: this.form,
			settings: this.settings()
		};
	}
}
